import { useState, useEffect } from 'react';
import './ATSDataViewer.css';

interface ATSData {
  fullName?: string;
  email?: string;
  phone?: string;
  location?: string;
  linkedinUrl?: string;
  websiteUrl?: string;
  professionalSummary?: string;
  careerObjective?: string;
  workExperience?: Array<{
    title?: string;
    company?: string;
    location?: string;
    startDate?: string;
    endDate?: string;
    duration?: string;
    employmentType?: string;
    description?: string;
    responsibilities?: string[];
    achievements?: string[];
    technologies?: string[];
    skills?: string[];
    projects?: string[];
    teamSize?: string;
    reportingTo?: string;
    budget?: string;
    revenue?: string;
    keyMetrics?: string[];
    industry?: string;
    companySize?: string;
    companyType?: string;
  }>;
  totalYearsExperience?: number;
  seniorityLevel?: string;
  skills?: {
    clinicalSkills?: string[];
    procedures?: string[];
    programmingLanguages?: string[];
    frameworks?: string[];
    databases?: string[];
    tools?: string[];
    cloudPlatforms?: string[];
    methodologies?: string[];
    management?: string[];
  };
  softSkills?: string[];
  certifications?: Array<{
    name?: string;
    issuer?: string;
    date?: string;
    expirationDate?: string;
    credentialId?: string;
    url?: string;
    status?: string;
  }>;
  education?: Array<{
    degree?: string;
    major?: string;
    school?: string;
    location?: string;
    startYear?: string;
    endYear?: string;
    year?: string;
    gpa?: string;
    honors?: string[];
    relevantCoursework?: string[];
    thesis?: string;
    advisor?: string;
  }>;
  languages?: string[];
  achievements?: string[];
  projects?: Array<{
    name?: string;
    description?: string;
    technologies?: string[];
    duration?: string;
    role?: string;
    teamSize?: string;
    achievements?: string[];
    url?: string;
    github?: string;
    status?: string;
  }>;
  publications?: string[];
  extractedDate?: string;
  confidenceScore?: number;
  confidenceReasons?: string[];
  atsComplianceScore?: number;
  atsComplianceReasons?: string[];
  rawText?: string;
}

interface Resume {
  id: string;
  filename: string;
  originalFilename?: string;
  atsData?: ATSData;
  atsScore?: number;
  atsProcessingStatus?: string;
  atsError?: string;
}

interface ATSDataViewerProps {
  resume: Resume;
  onClose: () => void;
}

export default function ATSDataViewer({ resume, onClose }: ATSDataViewerProps) {
  const [atsData, setAtsData] = useState<ATSData | null>(resume.atsData || null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (resume.atsData) {
      setAtsData(resume.atsData);
    } else {
      // If no ATS data is available, show error
      setError('No ATS data available for this resume');
    }
  }, [resume]);



  const renderContactInfo = () => {
    if (!atsData) return null;

    const contactFields = [
      { label: 'Name', value: atsData.fullName },
      { label: 'Email', value: atsData.email },
      { label: 'Phone', value: atsData.phone },
      { label: 'Location', value: atsData.location },
      { label: 'LinkedIn', value: atsData.linkedinUrl },
      { label: 'Website', value: atsData.websiteUrl }
    ].filter(field => field.value);

    if (contactFields.length === 0) return null;

    return (
      <div className="ats-section">
        <h3>👤 Contact Information</h3>
        <div className="contact-grid">
          {contactFields.map((field, index) => (
            <div key={index} className="contact-item">
              <span className="contact-label">{field.label}:</span>
              <span className="contact-value">
                {field.label === 'LinkedIn' || field.label === 'Website' ? (
                  <a href={field.value} target="_blank" rel="noopener noreferrer">
                    {field.value}
                  </a>
                ) : (
                  field.value
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderExperienceSummary = () => {
    if (!atsData) return null;

    return (
      <div className="ats-section">
        <h3>📊 Experience Summary</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Total Experience:</span>
            <span className="summary-value">{atsData.totalYearsExperience || 0} years</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Seniority Level:</span>
            <span className="summary-value">{atsData.seniorityLevel || 'Unknown'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Confidence Score:</span>
            <span className="summary-value">
              {atsData.confidenceScore ? `${Math.round(atsData.confidenceScore * 100)}%` : 'N/A'}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">ATS Compliance:</span>
            <span className="summary-value">
              {atsData.atsComplianceScore ? `${Math.round(atsData.atsComplianceScore * 100)}%` : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderProfessionalSummary = () => {
    if (!atsData?.professionalSummary) return null;

    return (
      <div className="ats-section">
        <h3>📝 Professional Summary</h3>
        <div className="summary-text">{atsData.professionalSummary}</div>
      </div>
    );
  };

  const renderWorkExperience = () => {
    if (!atsData?.workExperience || atsData.workExperience.length === 0) return null;

    return (
      <div className="ats-section">
        <h3>💼 Work Experience ({atsData.workExperience.length} positions)</h3>
        <div className="experience-list">
          {atsData.workExperience.map((exp, index) => (
            <div key={index} className="experience-item">
              <div className="exp-header">
                <div className="exp-title">{exp.title || 'Unknown Title'}</div>
                <div className="exp-company">{exp.company || 'Unknown Company'}</div>
                {exp.location && <div className="exp-location">{exp.location}</div>}
                <div className="exp-duration">
                  {exp.startDate || exp.duration} - {exp.endDate || 'Present'}
                </div>
                {exp.employmentType && <div className="exp-type">{exp.employmentType}</div>}
              </div>
              
              {exp.description && <div className="exp-description">{exp.description}</div>}
              
              {exp.responsibilities && exp.responsibilities.length > 0 && (
                <div className="exp-responsibilities">
                  <strong>Key Responsibilities:</strong>
                  <ul>
                    {exp.responsibilities.map((resp, respIndex) => (
                      <li key={respIndex}>{resp}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {exp.achievements && exp.achievements.length > 0 && (
                <div className="exp-achievements">
                  <strong>Achievements:</strong>
                  <ul>
                    {exp.achievements.map((achievement, achIndex) => (
                      <li key={achIndex}>{achievement}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {exp.technologies && exp.technologies.length > 0 && (
                <div className="exp-technologies">
                  <strong>Technologies:</strong>
                  <div className="tech-tags">
                    {exp.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {exp.skills && exp.skills.length > 0 && (
                <div className="exp-skills">
                  <strong>Skills Used:</strong>
                  <div className="skill-tags">
                    {exp.skills.map((skill, skillIndex) => (
                      <span key={skillIndex} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {exp.projects && exp.projects.length > 0 && (
                <div className="exp-projects">
                  <strong>Projects:</strong>
                  <ul>
                    {exp.projects.map((project, projIndex) => (
                      <li key={projIndex}>{project}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="exp-metadata">
                {exp.teamSize && <span className="metadata-item">Team Size: {exp.teamSize}</span>}
                {exp.reportingTo && <span className="metadata-item">Reports to: {exp.reportingTo}</span>}
                {exp.budget && <span className="metadata-item">Budget: {exp.budget}</span>}
                {exp.revenue && <span className="metadata-item">Revenue Impact: {exp.revenue}</span>}
                {exp.industry && <span className="metadata-item">Industry: {exp.industry}</span>}
                {exp.companySize && <span className="metadata-item">Company Size: {exp.companySize}</span>}
                {exp.companyType && <span className="metadata-item">Company Type: {exp.companyType}</span>}
              </div>
              
              {exp.keyMetrics && exp.keyMetrics.length > 0 && (
                <div className="exp-metrics">
                  <strong>Key Metrics:</strong>
                  <ul>
                    {exp.keyMetrics.map((metric, metricIndex) => (
                      <li key={metricIndex}>{metric}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTechnicalSkills = () => {
    if (!atsData?.skills) return null;

    // Handle skills as array (simple list)
    if (Array.isArray(atsData.skills)) {
      if (atsData.skills.length === 0) return null;
      return (
        <div className="ats-section">
          <h3>🛠️ Technical Skills</h3>
          <div className="skill-tags">
            {atsData.skills.map((skill, index) => (
              <span key={index} className="skill-tag">{skill}</span>
            ))}
          </div>
        </div>
      );
    }

    // Handle skills as object with categories
    if (typeof atsData.skills === 'object' && Object.keys(atsData.skills).length === 0) return null;

    return (
      <div className="ats-section">
        <h3>🛠️ Technical Skills</h3>
        <div className="skills-grid">
          {Object.entries(atsData.skills).map(([category, skills]) => {
            // Ensure skills is an array before mapping
            const skillsArray = Array.isArray(skills) ? skills : [];
            if (skillsArray.length === 0) return null;
            return (
              <div key={category} className="skill-category">
                <h4>{category.charAt(0).toUpperCase() + category.slice(1).replace(/([A-Z])/g, ' $1')}</h4>
                <div className="skill-tags">
                  {skillsArray.map((skill, index) => (
                    <span key={index} className="skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSoftSkills = () => {
    if (!atsData?.softSkills || atsData.softSkills.length === 0) return null;

    return (
      <div className="ats-section">
        <h3>🤝 Soft Skills</h3>
        <div className="skill-tags">
          {atsData.softSkills.map((skill, index) => (
            <span key={index} className="skill-tag soft-skill">{skill}</span>
          ))}
        </div>
      </div>
    );
  };

  const renderEducation = () => {
    if (!atsData?.education) return null;

    // Handle education as object (single entry) or array (multiple entries)
    let educationEntries: any[] = [];
    if (Array.isArray(atsData.education)) {
      educationEntries = atsData.education;
    } else if (typeof atsData.education === 'object') {
      // Single education object - wrap it in an array
      educationEntries = [atsData.education];
    }

    if (educationEntries.length === 0) return null;

    return (
      <div className="ats-section">
        <h3>🎓 Education ({educationEntries.length} {educationEntries.length === 1 ? 'entry' : 'entries'})</h3>
        <div className="education-list">
          {educationEntries.map((edu, index) => (
            <div key={index} className="education-item">
              <div className="edu-header">
                <div className="edu-degree">{edu.degree || edu.major || 'Unknown Degree'}</div>
                {edu.major && edu.major !== edu.degree && <div className="edu-major">{edu.major}</div>}
                <div className="edu-school">{edu.school || edu.institution || 'Unknown School'}</div>
                {edu.location && <div className="edu-location">{edu.location}</div>}
              </div>
              
              <div className="edu-dates">
                {(() => {
                  if (edu.years) return <span>{edu.years}</span>
                  if (edu.startYear && edu.endYear) return <span>{edu.startYear} - {edu.endYear}</span>
                  if (edu.year) return <span>{edu.year}</span>
                  return null
                })()}
              </div>
              
              {edu.gpa && <div className="edu-gpa">GPA: {edu.gpa}</div>}
              
              {(edu.honorSociety || (edu.honors && edu.honors.length > 0)) && (
                <div className="edu-honors">
                  <strong>Honors:</strong>
                  {edu.honorSociety ? (
                    <div>{edu.honorSociety}</div>
                  ) : (
                    <ul>
                      {edu.honors.map((honor: string, honorIndex: number) => (
                        <li key={honorIndex}>{honor}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              
              {edu.relevantCoursework && edu.relevantCoursework.length > 0 && (
                <div className="edu-coursework">
                  <strong>Relevant Coursework:</strong>
                  <div className="coursework-tags">
                    {edu.relevantCoursework.map((course: string, courseIndex: number) => (
                      <span key={courseIndex} className="coursework-tag">{course}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {edu.thesis && (
                <div className="edu-thesis">
                  <strong>Thesis:</strong> {edu.thesis}
                </div>
              )}
              
              {edu.advisor && (
                <div className="edu-advisor">
                  <strong>Advisor:</strong> {edu.advisor}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCertifications = () => {
    if (!atsData?.certifications || atsData.certifications.length === 0) return null;

    return (
      <div className="ats-section">
        <h3>🏆 Certifications ({atsData.certifications.length})</h3>
        <div className="certifications-list">
          {atsData.certifications.map((cert, index) => (
            <div key={index} className="certification-item">
              <div className="cert-header">
                <div className="cert-name">{typeof cert === 'string' ? cert : cert.name}</div>
                {typeof cert !== 'string' && cert.status && (
                  <div className={`cert-status ${cert.status.toLowerCase()}`}>{cert.status}</div>
                )}
              </div>
              
              {typeof cert !== 'string' && (
                <>
                  {cert.issuer && <div className="cert-issuer">Issued by: {cert.issuer}</div>}
                  
                  <div className="cert-dates">
                    {cert.date && <span>Issued: {cert.date}</span>}
                    {cert.expirationDate && <span>Expires: {cert.expirationDate}</span>}
                  </div>
                  
                  {cert.credentialId && (
                    <div className="cert-credential">Credential ID: {cert.credentialId}</div>
                  )}
                  
                  {cert.url && (
                    <a href={cert.url} target="_blank" rel="noopener noreferrer" className="cert-link">
                      🔗 Verify Certificate
                    </a>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLanguages = () => {
    if (!atsData?.languages || atsData.languages.length === 0) return null;

    return (
      <div className="ats-section">
        <h3>🌍 Languages</h3>
        <div className="skill-tags">
          {atsData.languages.map((language, index) => (
            <span key={index} className="skill-tag language">{language}</span>
          ))}
        </div>
      </div>
    );
  };

  const renderProjects = () => {
    if (!atsData?.projects || atsData.projects.length === 0) return null;

    return (
      <div className="ats-section">
        <h3>🚀 Projects ({atsData.projects.length})</h3>
        <div className="projects-list">
          {atsData.projects.map((project, index) => (
            <div key={index} className="project-item">
              <div className="project-header">
                <div className="project-name">{project.name || 'Untitled Project'}</div>
                {project.status && <div className="project-status">{project.status}</div>}
              </div>
              
              {project.description && <div className="project-description">{project.description}</div>}
              
              {project.technologies && project.technologies.length > 0 && (
                <div className="project-technologies">
                  <strong>Technologies:</strong>
                  <div className="tech-tags">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="project-metadata">
                {project.duration && <span className="metadata-item">Duration: {project.duration}</span>}
                {project.role && <span className="metadata-item">Role: {project.role}</span>}
                {project.teamSize && <span className="metadata-item">Team Size: {project.teamSize}</span>}
              </div>
              
              {project.achievements && project.achievements.length > 0 && (
                <div className="project-achievements">
                  <strong>Achievements:</strong>
                  <ul>
                    {project.achievements.map((achievement, achIndex) => (
                      <li key={achIndex}>{achievement}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="project-links">
                {project.url && (
                  <a href={project.url} target="_blank" rel="noopener noreferrer" className="project-link">
                    🌐 Live Demo
                  </a>
                )}
                {project.github && (
                  <a href={project.github} target="_blank" rel="noopener noreferrer" className="project-link">
                    📁 GitHub
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAchievements = () => {
    if (!atsData?.achievements || atsData.achievements.length === 0) return null;

    return (
      <div className="ats-section">
        <h3>🏅 Achievements</h3>
        <ul className="achievements-list">
          {atsData.achievements.map((achievement, index) => (
            <li key={index} className="achievement-item">{achievement}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderPublications = () => {
    if (!atsData?.publications || atsData.publications.length === 0) return null;

    return (
      <div className="ats-section">
        <h3>📚 Publications</h3>
        <ul className="publications-list">
          {atsData.publications.map((publication, index) => (
            <li key={index} className="publication-item">{publication}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderMetadata = () => {
    if (!atsData) return null;

    return (
      <div className="ats-section">
        <h3>📋 Metadata</h3>
        <div className="metadata-grid">
          <div className="metadata-item">
            <span className="metadata-label">Extracted Date:</span>
            <span className="metadata-value">{atsData.extractedDate || 'Unknown'}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Confidence Score:</span>
            <span className="metadata-value">
              {atsData.confidenceScore ? `${Math.round(atsData.confidenceScore * 100)}%` : 'N/A'}
            </span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">ATS Compliance:</span>
            <span className="metadata-value">
              {atsData.atsComplianceScore ? `${Math.round(atsData.atsComplianceScore * 100)}%` : 'N/A'}
            </span>
          </div>
        </div>
        
        {atsData.confidenceReasons && atsData.confidenceReasons.length > 0 && (
          <div className="reasons-section">
            <h4>Confidence Reasons:</h4>
            <ul>
              {atsData.confidenceReasons.map((reason, index) => (
                <li key={index}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
        
        {atsData.atsComplianceReasons && atsData.atsComplianceReasons.length > 0 && (
          <div className="reasons-section">
            <h4>ATS Compliance Reasons:</h4>
            <ul>
              {atsData.atsComplianceReasons.map((reason, index) => (
                <li key={index}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };


  if (error) {
    return (
      <>
        <div className="ats-modal-overlay" onClick={onClose}></div>
        <div className="ats-modal-content">
          <div className="ats-modal-header">
            <h2>Error Loading ATS Data</h2>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          <div className="ats-modal-body">
            <div className="error-message">{error}</div>
          </div>
          <div className="ats-modal-footer">
            <button onClick={onClose} className="btn btn-primary">Close</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="ats-modal-overlay" onClick={onClose}></div>
      <div className="ats-modal-content">
        <div className="ats-modal-header">
          <h2>ATS Data - {resume.originalFilename || resume.filename}</h2>
          <div className="header-controls">
            <button onClick={onClose} className="close-btn">×</button>
          </div>
        </div>
        
        <div className="ats-modal-body">
          <div className="ats-data-content">
            {renderContactInfo()}
            {renderExperienceSummary()}
            {renderProfessionalSummary()}
            {renderWorkExperience()}
            {renderTechnicalSkills()}
            {renderSoftSkills()}
            {renderEducation()}
            {renderCertifications()}
            {renderLanguages()}
            {renderProjects()}
            {renderAchievements()}
            {renderPublications()}
            {renderMetadata()}
          </div>
        </div>
        
        <div className="ats-modal-footer">
          <button onClick={onClose} className="btn btn-primary">Close</button>
        </div>
      </div>
    </>
  );
}
